import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logError } from "../../utils/customLogger";
import { getBearerToken } from "./bearerToken";

/** POST body for backend `pharmacy/appeal/not-required`. */
export interface AppealNotRequiredPayload {
  identifier: string;
  reason: string;
}

/** Parsed response from backend `pharmacy/appeal/not-required`. */
export interface AppealNotRequiredResponse {
  message?: string;
  success?: boolean;
  identifier?: string;
}

/**
 * Records that a case does not require an appeal.
 *
 * @param payload - Order identifier and user-provided reason
 * @returns Parsed JSON response from backend service
 * @throws {Error} When auth is missing or the API returns a non-OK status
 */
export const appealNotRequired = async (
  payload: AppealNotRequiredPayload,
): Promise<AppealNotRequiredResponse> => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const response = await fetch(API_ENDPOINTS.APPEAL_NOT_REQUIRED, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    const responseText = await response.text();
    let parsed: AppealNotRequiredResponse = {};

    if (responseText.trim()) {
      try {
        parsed = JSON.parse(responseText) as AppealNotRequiredResponse;
      } catch {
        parsed = { message: responseText };
      }
    }

    if (!response.ok) {
      throw new Error(
        parsed.message ||
          `Failed to submit appeal feedback: ${response.status} - ${responseText}`,
      );
    }

    return parsed;
  } catch (error) {
    logError(error as Error, "appealNotRequired");
    throw error;
  }
};
